import {
    usePortfolioSelection,
    usePortfolioStreamedOrSelectedPositionFromOrders,
    usePortfolioStreamedOrSelectedSymbolQuantityFromOrders,
} from 'portfolio/store/hooks';
import { useSelectConfiguredSymbolForWidgetOrItsCapsuleContainer } from 'widgets/tiles/store/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';

export interface SymbolBinding {
    symbol?: string;
    /*
     * True if this symbol is linked to the portfolio selection
     */
    linked: boolean;
    symbology: SymbologyValues;
}

export function useCapsuleBoundSymbol(): SymbolBinding {
    const configSymbol =
        useSelectConfiguredSymbolForWidgetOrItsCapsuleContainer();
    const selection = usePortfolioSelection();
    const configSymbology = useSelectAppSettings().symbology;
    return selectCapsuleBoundSymbol(configSymbol, configSymbology, selection);
}

export function selectCapsuleBoundSymbol(
    configSymbol: string | undefined,
    configSymbology: SymbologyValues,
    portfolioSelection: PortfolioSelectionWrapper
) {
    let symbol: string | undefined;
    let symbology: SymbologyValues;

    const portfolioSelectedSymbol = portfolioSelection.selectedSymbol;
    const portfolioSelectionSymbology = portfolioSelection.symbology;

    if (configSymbol) {
        symbol = configSymbol;
        symbology = configSymbology;
    } else {
        symbol = portfolioSelectedSymbol;
        symbology = portfolioSelectionSymbology;
    }

    const linked = !configSymbol && !!portfolioSelectedSymbol;
    return { symbol, linked, symbology };
}

export function useWarningForOrderDependentCapsules(): string | undefined {
    const pinnedSymbol =
        useSelectConfiguredSymbolForWidgetOrItsCapsuleContainer();
    const position = usePortfolioStreamedOrSelectedPositionFromOrders();
    const { selectedOrders } = usePortfolioSelection();

    if (pinnedSymbol) {
        return 'Not available in a pinned capsule container';
    }

    if (position) {
        // We have a position from orders => no warning
        return undefined;
    }

    if (selectedOrders.length === 0) {
        return 'Select at least one order';
    }

    return 'Multiple positions selected';
}

export function useWarningForOrderDependentCapsulesSupportingSymbolQuantity():
    | string
    | undefined {
    const pinnedSymbol =
        useSelectConfiguredSymbolForWidgetOrItsCapsuleContainer();
    const symbolQuantity =
        usePortfolioStreamedOrSelectedSymbolQuantityFromOrders();
    const { selectedOrders } = usePortfolioSelection();

    if (pinnedSymbol) {
        return 'Not available in a pinned capsule container';
    }

    if (symbolQuantity) {
        // We have a position from orders => no warning
        return undefined;
    }

    if (selectedOrders.length === 0) {
        return 'Select at least one order';
    }

    return 'Multiple symbols selected';
}
